import { useState } from 'react';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { Home, Users, Video, User, Search, MessageCircle, Menu, Bookmark, Calendar, TrendingUp, Music, Church, Award, X, LogOut, Globe, Settings, BookOpen, Shield, UserPlus, Waves, ShoppingBag, Sparkles, Heart } from 'lucide-react';
import Logo3D from './Logo3D';
import NotificationsDropdown from './notifications/NotificationsDropdown';
import SuggestedDevotees from './friends/SuggestedDevotees';
import { useUI } from '@/contexts/UIContext';

export const FacebookLayout = ({ user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const { hideBottomNav } = useUI();

  const topNavIcons = [
    { icon: Home, path: '/', label: 'Home' },
    { icon: Waves, path: '/flow', label: 'Flow' },
    { icon: UserPlus, path: '/discover', label: 'Find Devotees' },
    { icon: Users, path: '/pandit', label: 'Pandits' },
    { icon: Church, path: '/live', label: 'Live' },
  ];

  // Clean mobile navigation - exactly 5 items: Home, Granth, Flow, Messages, Shop
  const mobileNavIcons = [
    { icon: Home, path: '/', label: 'Home' },
    { icon: BookOpen, path: '/ai-granth', label: 'Granth' },
    { icon: Waves, path: '/flow', label: 'Flow', isCenter: true },
    { icon: MessageCircle, path: '/messages', label: 'Messages' },
    { icon: ShoppingBag, path: '/shop', label: 'Shop' },
  ];

  const leftMenuItems = [
    { icon: User, label: user?.name || 'Profile', path: '/profile', color: 'text-blue-600', avatar: user?.avatar },
    { icon: Globe, label: 'Discover Devotees', path: '/discover', color: 'text-green-600' },
    { icon: Waves, label: 'Flow (Pravah)', path: '/flow', color: 'text-orange-500' },
    { icon: MessageCircle, label: 'Messages', path: '/messages', color: 'text-blue-500' },
    { icon: Users, label: 'Find Pandit Ji', path: '/pandit', color: 'text-orange-600' },
    { icon: Calendar, label: 'My Pooja Bookings', path: '/pandit/bookings', color: 'text-purple-600' },
    { icon: BookOpen, label: 'ग्रन्थ (Sacred Texts)', path: '/ai-granth', color: 'text-amber-700' },
    { icon: Sparkles, label: 'ज्योतिष (Astrology)', path: '/jyotish', color: 'text-purple-600' },
    { icon: Heart, label: 'विवाह (Matrimony)', path: '/vivah', color: 'text-rose-600' },
    { icon: Bookmark, label: 'Saved', path: '/saved', color: 'text-purple-600' },
    { icon: Church, label: 'Live Aarti', path: '/live', color: 'text-red-600' },
    { icon: Music, label: 'Bhakti Music', path: '/music', color: 'text-green-600' },
    { icon: Video, label: 'Bhakti Videos', path: '/videos', color: 'text-pink-600' },
    { icon: TrendingUp, label: 'Trending', path: '/trending', color: 'text-cyan-600' },
    { icon: Settings, label: 'Settings', path: '/settings', color: 'text-gray-600' },
    { icon: Shield, label: 'Admin Panel', path: '/admin/login', color: 'text-red-700', isAdmin: true },
    { icon: LogOut, label: 'Logout', path: '#logout', color: 'text-gray-500', isLogout: true },
  ];

  // Add Pandit Dashboard for pandit users
  if (user?.role === 'pandit') {
    leftMenuItems.splice(5, 0, { icon: Award, label: 'Pandit Dashboard', path: '/pandit/dashboard', color: 'text-amber-600' });
  }

  if (user?.role === 'creator') {
    leftMenuItems.splice(-2, 0, { icon: Award, label: 'Creator Studio', path: '/creator-dashboard', color: 'text-amber-600' });
  }

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-100 w-full max-w-[100vw] overflow-x-hidden">
      {/* Top Navigation Bar - Clean: Search + Notifications only */}
      <header className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50 h-14" style={{ overflow: 'visible' }}>
        <div className="flex items-center justify-between h-full px-2 sm:px-4 max-w-screen-2xl mx-auto w-full">
          {/* Left: Logo */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button 
              onClick={() => navigate('/')} 
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              data-testid="logo-btn"
            >
              <Logo3D size="sm" />
              <span className="text-lg sm:text-xl font-bold text-[#FF9933] hidden sm:block"
                    style={{ fontFamily: 'Merriweather, serif' }}>
                DharmBook
              </span>
            </button>
          </div>

          {/* Center: Main Nav Icons (Desktop only) */}
          <div className="hidden md:flex items-center gap-1 flex-1 justify-center max-w-xl">
            {topNavIcons.map((item, index) => {
              const active = isActive(item.path);
              return (
                <button
                  key={index}
                  onClick={() => navigate(item.path)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`relative flex-1 max-w-[100px] py-3 rounded-lg transition-all ${
                    active
                      ? 'text-[#FF9933]'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex justify-center">
                    <item.icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                  {active && (
                    <div className="absolute bottom-0 left-2 right-2 h-[3px] bg-[#FF9933] rounded-t"></div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Right: Search + Shop + Notifications + Profile + Menu */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Mobile Search Button */}
            <button 
              onClick={() => setShowSearch(!showSearch)}
              className="p-2 rounded-full bg-gray-100 hover:bg-gray-200"
              data-testid="search-btn"
            >
              <Search className="w-5 h-5 text-gray-600" />
            </button>

            {/* Desktop Search */}
            <div className="relative hidden lg:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search DharmBook"
                className="pl-10 pr-4 py-2 w-48 xl:w-60 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>

            {/* Find Devotees Button (Mobile only - desktop has it in top nav) */}
            <button
              onClick={() => navigate('/discover')}
              className="md:hidden p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              data-testid="find-friends-btn"
              title="Find Devotees"
            >
              <UserPlus className="w-5 h-5 text-gray-700" />
            </button>

            {/* Notifications */}
            <NotificationsDropdown />

            {/* Menu Button */}
            <button 
              onClick={() => setShowMobileMenu(true)}
              className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              data-testid="menu-btn"
            >
              <Menu className="w-5 h-5 text-gray-700" />
            </button>
          </div>
        </div>

        {/* Mobile Search Bar */}
        {showSearch && (
          <div className="absolute top-14 left-0 right-0 bg-white p-3 border-b shadow-sm w-full z-50">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search DharmBook"
                autoFocus
                className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
          </div>
        )}
      </header>

      {/* Mobile Menu Overlay */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-[60] bg-black/50" onClick={() => setShowMobileMenu(false)}>
          <div 
            className="absolute right-0 top-0 w-[280px] max-w-[85vw] bg-white shadow-xl flex flex-col"
            style={{ height: '100dvh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-900">Menu</h2>
              <button 
                onClick={() => setShowMobileMenu(false)}
                className="p-2 rounded-full hover:bg-gray-100"
                data-testid="close-menu-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* User Profile Card */}
            <div className="p-4 border-b flex-shrink-0">
              <button 
                onClick={() => { navigate('/profile'); setShowMobileMenu(false); }}
                className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-gray-50"
              >
                {user?.avatar && !user?.avatar.includes('ui-avatars.com') ? (
                  <img src={user.avatar} alt={user?.name} className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%)',
                      backgroundSize: '200% 200%',
                      animation: 'avatarShimmer 2s ease-in-out infinite'
                    }}
                  >
                    <User className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                <div className="text-left min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{user?.name}</h3>
                  <p className="text-sm text-gray-500">View your profile</p>
                </div>
              </button>
            </div>

            {/* Menu Items - Scrollable */}
            <nav className="p-2 flex-1 overflow-y-auto min-h-0">
              {leftMenuItems.slice(1).filter(item => !item.isLogout).map((item, index) => {
                const active = isActive(item.path);
                return (
                  <button
                    key={index}
                    onClick={() => { 
                      if (item.isAdmin) {
                        window.location.href = '/admin/login';
                      } else {
                        navigate(item.path);
                      }
                      setShowMobileMenu(false); 
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      active ? 'bg-orange-50 text-[#FF9933]' : 'hover:bg-gray-50'
                    } ${item.isAdmin ? 'mt-2 border-t pt-4' : ''}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                      active ? 'bg-orange-100' : item.isAdmin ? 'bg-red-100' : 'bg-gray-100'
                    }`}>
                      <item.icon className={`w-5 h-5 ${active ? 'text-[#FF9933]' : item.color}`} />
                    </div>
                    <span className={`font-medium ${item.isAdmin ? 'text-red-700' : 'text-gray-900'}`}>{item.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Logout Button - Fixed at bottom */}
            <div className="p-3 border-t bg-white flex-shrink-0" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
              <button
                onClick={() => { onLogout(); setShowMobileMenu(false); }}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-red-50 hover:bg-red-100 transition-colors border border-red-200"
                data-testid="mobile-logout-btn"
              >
                <LogOut className="w-5 h-5 text-red-600" />
                <span className="font-medium text-red-700">Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="pt-14 pb-20 md:pb-0 w-full max-w-[100vw] overflow-x-hidden">
        <div className="max-w-screen-2xl mx-auto flex w-full">
          {/* Left Sidebar (Desktop) */}
          <aside className="hidden lg:block w-[280px] xl:w-[320px] fixed left-0 top-14 bottom-0 overflow-y-auto p-2">
            <nav className="space-y-1">
              {leftMenuItems.filter(item => !item.isLogout).map((item, index) => {
                const active = isActive(item.path);
                return (
                  <button
                    key={index}
                    onClick={() => {
                      if (item.isAdmin) {
                        window.location.href = '/admin/login';
                      } else {
                        navigate(item.path);
                      }
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      active ? 'bg-gray-200' : 'hover:bg-gray-200'
                    } ${item.isAdmin ? 'mt-3 border-t border-gray-200 pt-4' : ''}`}
                    data-testid={item.isAdmin ? 'admin-panel-link' : undefined}
                  >
                    {item.avatar ? (
                      <img src={item.avatar} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <item.icon className={`w-7 h-7 ${item.color} flex-shrink-0`} strokeWidth={1.5} />
                    )}
                    <span className={`text-[15px] font-medium truncate ${item.isAdmin ? 'text-red-700' : 'text-gray-900'}`}>{item.label}</span>
                  </button>
                );
              })}

              <div className="border-t border-gray-300 my-3"></div>

              <button
                onClick={onLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-200"
                data-testid="logout-btn"
              >
                <LogOut className="w-7 h-7 text-gray-600 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-[15px] font-medium text-gray-900">Logout</span>
              </button>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 lg:ml-[280px] xl:ml-[320px] xl:mr-[320px] min-h-[calc(100vh-3.5rem)] w-full overflow-x-hidden">
            <Outlet />
          </main>

          {/* Right Sidebar (Desktop) */}
          <aside className="hidden xl:block w-[320px] fixed right-0 top-14 bottom-0 overflow-y-auto p-4">
            {/* Suggested Devotees - Live */}
            <SuggestedDevotees />

            <div className="border-t border-gray-300 my-4"></div>

            {/* Sponsored */}
            <div>
              <h3 className="text-[17px] font-semibold text-gray-500 mb-3">Sponsored</h3>
              <div className="space-y-3">
                <a href="#" className="flex gap-3 p-2 -mx-2 rounded-lg hover:bg-gray-200 transition-colors">
                  <img 
                    src="https://images.unsplash.com/photo-1723707358389-7b47690dff16?w=150&h=150&fit=crop" 
                    alt="Ad" 
                    className="w-[120px] h-[120px] rounded-lg object-cover flex-shrink-0" 
                  />
                  <div className="pt-1 min-w-0">
                    <p className="text-[15px] font-medium text-gray-900">Spiritual Retreat</p>
                    <p className="text-[13px] text-gray-500">peaceful-retreats.com</p>
                  </div>
                </a>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile Bottom Navigation - Zepto Style with DharmShop Colors */}
      {!hideBottomNav && !showMobileMenu && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 shadow-[0_-2px_10px_rgba(0,0,0,0.05)] z-50 w-full" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}>
          <div className="grid grid-cols-5 h-16">
            {mobileNavIcons.map((item, index) => {
              const active = isActive(item.path);
              const isShop = item.path === '/shop';
              
              // Center Flow button with special design
              if (item.isCenter) {
                return (
                  <button
                    key={index}
                    onClick={() => navigate(item.path)}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                    className="flex items-center justify-center relative"
                  >
                    <div 
                      className="absolute -top-4 w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
                      style={{
                        background: 'linear-gradient(135deg, #FF9933 0%, #FF6600 50%, #FF9933 100%)',
                        boxShadow: '0 4px 15px rgba(255, 153, 51, 0.4)'
                      }}
                    >
                      <Waves className="w-7 h-7 text-white" strokeWidth={2.5} />
                    </div>
                  </button>
                );
              }
              
              return (
                <button
                  key={index}
                  onClick={() => navigate(item.path)}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  className={`flex flex-col items-center justify-center transition-all ${
                    active 
                      ? isShop ? 'text-amber-600' : 'text-[#FF9933]' 
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <div className={`relative ${active ? 'transform scale-110' : ''} transition-transform`}>
                    <item.icon 
                      className={`w-6 h-6 ${isShop && active ? 'text-amber-600' : ''}`} 
                      strokeWidth={active ? 2 : 1.5} 
                    />
                  </div>
                  <span className={`text-[10px] mt-0.5 font-medium ${
                    active 
                      ? isShop ? 'text-amber-600' : 'text-[#FF9933]'
                      : 'text-gray-400'
                  }`}>
                    {item.label}
                  </span>
                  {active && (
                    <div 
                      className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full"
                      style={{ background: isShop ? '#d97706' : '#FF9933' }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </nav>
      )}

      <style>{`
        .safe-area-bottom {
          padding-bottom: env(safe-area-inset-bottom, 0);
        }
        @keyframes avatarShimmer {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
    </div>
  );
};

export default FacebookLayout;
